import { defineStore } from 'pinia';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { ref, Ref } from 'vue';
import { createResourceUri } from '@/utils/links';
import { AgentURI } from '@/types/agent';
import apiGw from '@/services/apiGw';
import { createResourceLoadingMixin } from '@/store/resourceLoadingMixin';
import { YapListItem } from '@/components/YapList.vue';

export const conditionsRefdataCollection = 'conditions';

const resourceLoadingMixin = createResourceLoadingMixin();

interface Contract {
  acceptedConditionCodes: ConditionCodes[];
  contractStatus: string;
  contractType: string;
  contractingDate: string;
  createdAt: string;
  productCode: string;
  status: string;
  terminationDate: string;
  typeName: ContractTypes;
  uid: string;
}

export enum ConditionCodes {
  AuthenticInformationConfirmation = 'AIC',
  BeneficialOwner = 'BOD',
  CasinoTerms = 'CSTC',
  DeepCloudTerms = 'DCTC',
  PrivacyPolicy = 'DPP',
  ContactDataDisclosure = 'DSC',
  DataSharing = 'DSTC',
  EBillTermsConditions = 'EBSTC',
  BusinessRisks = 'FTR',
  TermsConditions = 'GTC',
  AssetProtection = 'NOAP',
  NonUS = 'NONUSP',
  PensionServiceTerms = 'PSAG',
  ReverseSolicitation = 'PSC',
  Yap3aTerms = 'PSTC',
  PartnershipTerms = 'PTC',
  SwisscomSigningTerms = 'SCTC',
  TaxCompliance = 'TCC',
  WiseTerms = 'YPWC',
  YapiniTermsConditions = 'YPYC'
}

enum ContractTypes {
  DataSharingContract = 'DataSharingContract'
}

const hiddenContracts = [ContractTypes.DataSharingContract];
const hiddenConditions = [ConditionCodes.DataSharing];

export const useAgreementsStore = defineStore('contracts', () => {
  const contextSwitchStore = useContextSwitchStore();

  // STATES
  const acceptedConditionCodes: Ref<string[]> = ref([]);
  const conditionsLong: Ref<Record<string, string>> = ref({});
  const conditionsShort: Ref<Record<string, string>> = ref({});
  const contracts: Ref<Contract[]> = ref([]);
  const hasCashAccountAgreementBeenAccepted = ref(false);

  // GETTERS
  function getConditionsAsYapList(): YapListItem[] {
    const ctxUid = contextSwitchStore.currentContextUid;
    if (ctxUid) {
      if (acceptedConditionCodes.value.length > 0) {
        return acceptedConditionCodes.value.map((condition) => {
          return {
            key: condition,
            title: `${conditionsRefdataCollection}.${condition.toLowerCase()}`,
            icon: getConditionIcon(condition)
          };
        });
      }
    }
    return [];
  }

  function getConditionIcon(conditionCodes: string): string {
    let icon: string;
    switch (conditionCodes) {
      case ConditionCodes.BeneficialOwner:
        icon = 'export-outline';
        break;
      case ConditionCodes.BusinessRisks:
        icon = 'risks-outline';
        break;
      case ConditionCodes.EBillTermsConditions:
        icon = 'export-outline';
        break;
      case ConditionCodes.YapiniTermsConditions:
        icon = 'export-outline';
        break;
      case ConditionCodes.TermsConditions:
        icon = 'terms-outline';
        break;
      case ConditionCodes.AssetProtection:
        icon = 'asset-protection-outline';
        break;
      case ConditionCodes.PrivacyPolicy:
        icon = 'shield-outline';
        break;
      case ConditionCodes.TaxCompliance:
        icon = 'export-outline';
        break;
      case ConditionCodes.NonUS:
        icon = 'no-person-outline';
        break;
      default:
        icon = 'terms-outline'; // Default icon if no specific icon is defined
    }
    return icon;
  }

  // ACTIONS
  function setCashAccountAgreementAccepted(accepted: boolean) {
    hasCashAccountAgreementBeenAccepted.value = accepted;
  }

  async function loadContracts(forceReload = false) {
    const contextUid = contextSwitchStore.currentContextUid;

    if (!contextUid) {
      console.warn('no context provided...');
      return;
    }

    try {
      if (forceReload) {
        resourceLoadingMixin.removeResource(`loadContracts-${contextUid}`);
      }
      const uri = createResourceUri(AgentURI.AgentV2, {
        uid: contextSwitchStore.currentContextUid,
        resource: AgentURI.Contracts
      });

      await resourceLoadingMixin.loadResource(
        `loadContracts-${contextUid}`,
        async () => {
          const response = await apiGw.get(uri);

          addContractsToState(response.data.contracts);

          // Filter out empty condition codes before adding to state
          const filteredConditions = response.data.acceptedConditionCodes.filter(
            (condition: { toString: () => string }) => condition !== ''
          );

          addConditionsToState(filteredConditions, response.data.conditionsLong, response.data.conditionsShort);
        },
        forceReload
      );
    } catch (error) {
      console.error('Error loading contracts:', error);
    }
  }
  function addContractsToState(loadedContracts: Contract[]) {
    const ctxUid = contextSwitchStore.currentContextUid;
    if (ctxUid) {
      const newContracts = loadedContracts.filter((c) => !hiddenContracts.includes(c.typeName));
      contracts.value = newContracts;
    }
  }
  function addConditionsToState(
    loadedAcceptedConditions: ConditionCodes[],
    loadedConditionsLong: Record<ConditionCodes, string>,
    loadedConditionsShort: Record<ConditionCodes, string>
  ) {
    const ctxUid = contextSwitchStore.currentContextUid;
    if (ctxUid) {
      loadedAcceptedConditions = loadedAcceptedConditions
        .filter((condition) => !hiddenConditions.includes(condition))
        .sort();
      acceptedConditionCodes.value = loadedAcceptedConditions;
      conditionsLong.value = loadedConditionsLong;
      conditionsShort.value = loadedConditionsShort;
    }
  }

  function $reset() {
    acceptedConditionCodes.value = [];
    conditionsLong.value = {};
    conditionsShort.value = {};
    contracts.value = [];
    hasCashAccountAgreementBeenAccepted.value = false;
  }

  return {
    // STATES
    acceptedConditionCodes,
    conditionsLong,
    conditionsShort,
    contracts,
    hasCashAccountAgreementBeenAccepted,
    // GETTERS
    getConditionsAsYapList,
    // ACTIONS
    setCashAccountAgreementAccepted,
    loadContracts,
    $reset
  };
});
